import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DialogComponent} from "@syncfusion/ej2-angular-popups";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";
import {InternationalizationService} from "../../../services/internationalization/internationalization.service";
import {UserService} from "../../../services/user/user.service";
import {DatePickerComponent, DateTimePickerComponent} from "@syncfusion/ej2-angular-calendars"
import { DateTime } from "luxon";

@Component({
  selector: 'sb-kanban-due-date-picker',
  templateUrl: './kanban-due-date-picker.component.html',
  styleUrls: ['./kanban-due-date-picker.component.scss']
})
export class KanbanDueDatePickerComponent implements OnInit, AfterViewInit {
  @ViewChild('dialogComponent') dialogComponent: DialogComponent
  @ViewChild('dueDatePicker') dueDatePicker: DatePickerComponent | DateTimePickerComponent;

  @Input()
  dueDate: Date;

  @Input()
  readOnly : boolean;

  @Input()
  showTime: boolean;

  dueDateFormatted: string;

  @Input()
  addDueDateLabel: string;

  @Input()
  fieldDisplayName: string;

  @Output("dueDateChanged")
  dueDateChanged: EventEmitter<Date | null> = new EventEmitter()

  showModal: boolean = false;
  dueDateInUserTimezone: Date;

  public buttons: ButtonPropsModel[] = [
    {
      'click': this.hideDialog.bind(this, false),
      buttonModel: {
        content: 'Cancel',
      }
    },
    {
      'click': this.hideDialog.bind(this, true),
      buttonModel: {
        content: 'Save',
        isPrimary: true,
      }
    },
  ];

  constructor(private i18nService: InternationalizationService, private userService: UserService) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.dialogComponent.element.addEventListener('focusout', (event) => {
      // Check if the focus is outside the dialog
      if (!this.dialogComponent.element.contains(event.relatedTarget as Element)) {
        // Close the dialog when it loses focus
        this.hideDialog(false);
      }
    });
  }

  ngOnChanges(change: SimpleChanges) {
    if (change['showTime'] || change['dueDate']) {
      if (this.showTime) {
        this.dueDateInUserTimezone = this.dueDate ? this.convertDateToUserTZ(this.dueDate) : null;
      } else {
        this.dueDateInUserTimezone = this.dueDate;
      }
      this.dueDateFormatted = this.dueDateInUserTimezone ? this.formatDate(this.dueDateInUserTimezone) : '';
    }
  }

  public show(event: MouseEvent): void {
    if (event) {
      const {xPosition, yPosition} = this.computePosition(event);
      this.dialogComponent.position = { X: xPosition, Y: yPosition };
    }

    this.showModal = true;
  }

  private computePosition(event: MouseEvent) {
    const offsetX = event.clientX;
    const offsetY = event.clientY;
    const dialogWidth = Number(this.dialogComponent.width);
    const dialogHeight = Number(this.dialogComponent.height);

    // Calculate the position relative to the window size
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Adjust dialog position to prevent it from being hidden
    const xPosition = offsetX < windowWidth - dialogWidth ? offsetX : windowWidth - dialogWidth;
    const yPosition = offsetY < windowHeight - dialogHeight ? offsetY : windowHeight - dialogHeight;
    return {xPosition, yPosition};
  }

  hideDialog(save: boolean) {
    if (save) {
      if (this.showTime) {
        // Offset User Timezone - Syncfusion will keep picker date as browser timezone, we want to keep the same local time but expressed in user Timezone
        const tzAdjustedDate = DateTime.fromJSDate(this.dueDatePicker.value).setZone(this.userService.getTimeZone(), {keepLocalTime: true}).toJSDate()
        this.dueDateChanged.emit(tzAdjustedDate);
      } else {
        this.dueDateChanged.emit(this.dueDatePicker.value);
      }
    }
    this.clear();
    this.showModal = false;
  }

  onDueDateClick(e: MouseEvent) {
    if (!this.readOnly) {
      this.show(e)
    }
  }

  private clear() {
    this.dueDatePicker.value = this.dueDateInUserTimezone
  }

  private convertDateToUserTZ(date: Date): Date {
    const timezone = this.userService.getTimeZone();
    return this.i18nService.toLocaleDateOfTimezone(date, timezone);
  }

  private formatDate(date: Date) {
    const dateSkeleton = date.getFullYear() === new Date().getFullYear() ? 'MMMEd' :'yMMMEd'
    const dateFormatOptions = {type: 'date', skeleton: dateSkeleton}
    const dateFormatted = this.i18nService.formatDate(date, dateFormatOptions)
    if (this.showTime) {
      return dateFormatted + " " + this.i18nService.formatShortTime(date);
    } else {
      return dateFormatted;
    }
  }
}
